const PENDING_INVITE_CODE_KEY = 'pending_invite_code';

// A pending invite only has to survive the register/login detour; after a day it is more likely stale than wanted.
const PENDING_INVITE_TTL_MS = 24 * 60 * 60 * 1000;

// The backend generates 8 characters from A–Z0–9 (generate_invite_code in backend/app/api/classes.py). Accept a
// slightly wider range so a mistyped code still reaches the backend and gets its "invalid invite" answer, while
// paths that can't be a code (dots, dashes, long words) are a 404 instead of a failed invite.
const INVITE_CODE_PATTERN = /^[A-Z0-9]{6,12}$/;

export function normalizeInviteCode(inviteCode: string | null | undefined): string | null {
  if (!inviteCode) {
    return null;
  }

  const normalized = inviteCode.trim().toUpperCase();
  if (!normalized || !INVITE_CODE_PATTERN.test(normalized)) {
    return null;
  }

  return normalized;
}

export function getInviteLink(inviteCode: string): string {
  if (typeof window === 'undefined') {
    return `/${inviteCode}`;
  }

  return `${window.location.origin}/${inviteCode}`;
}

export function setPendingInviteCode(inviteCode: string): void {
  const normalized = normalizeInviteCode(inviteCode);
  if (!normalized) {
    return;
  }

  localStorage.setItem(PENDING_INVITE_CODE_KEY, JSON.stringify({ code: normalized, expiresAt: Date.now() + PENDING_INVITE_TTL_MS }));
}

export function getPendingInviteCode(): string | null {
  const value = localStorage.getItem(PENDING_INVITE_CODE_KEY);
  if (!value) {
    return null;
  }

  try {
    const { code, expiresAt } = JSON.parse(value) as { code?: string; expiresAt?: number };
    if (typeof expiresAt === 'number' && expiresAt > Date.now()) {
      return normalizeInviteCode(code);
    }
  } catch {
    // A bare code stored by an older version: it has no expiry, so drop it.
  }

  localStorage.removeItem(PENDING_INVITE_CODE_KEY);
  return null;
}

export function clearPendingInviteCode(): void {
  localStorage.removeItem(PENDING_INVITE_CODE_KEY);
}
